// Modal: lançamento de notas por área (prova objetiva) — com conferência visual quando há PDF.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal } from '../ui/Modal';
import { styles } from '../../styles/globalStyles';
import { apiClient } from '../../api/apiClient';
import { useNotifications } from '../../contexts/NotificationContext';
import { DEFINITIVE_ANSWER_KEY_REQUIRED_MSG } from '../../utils/examAnswerKeyPolicy';
import { ExamObjectiveOverlayPanel } from './ExamObjectiveOverlayPanel';
import {
  areaScoreMax,
  computeExamFinalPreview,
  gradeEntryHelpText,
  objectiveFinalDisplay,
  objectiveScoreModeLabel,
  savedGradesNotificationText,
} from '../../utils/examObjectiveScoring';

const cardStyle = {
  padding: '14px 16px',
  borderRadius: '10px',
  border: '1px solid #E5E7EB',
  background: '#FFFFFF',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
};

export const ExamGradeEntryModal = React.memo(
  ({
    isOpen,
    application,
    onClose,
    onSaved,
    definitiveAnswerKeyReady = true,
    processId = null,
  }) => {
    const { addNotification } = useNotifications();
    const [scores, setScores] = useState({});
    const [saving, setSaving] = useState(false);

    const hasScan = Boolean(application?.exam_objective_scan_url);
    const scoreMode = application?.exam_objective_score_mode;
    const hasEssay = Boolean(application?.exam_uses_essay);
    const objectiveMax = application?.exam_objective_max_points;
    const pid = processId ?? application?.process_id;

    const rows = useMemo(() => {
      const w = application?.course_exam_area_weights;
      return Array.isArray(w) ? w.filter((r) => (r?.area || '').trim()) : [];
    }, [application]);

    useEffect(() => {
      if (!isOpen || !application) return;
      const prev =
        application.exam_area_scores && typeof application.exam_area_scores === 'object'
          ? application.exam_area_scores
          : {};
      const init = {};
      rows.forEach((r) => {
        const a = (r.area || '').trim();
        if (prev[a] != null && prev[a] !== '') init[a] = String(prev[a]).replace('.', ',');
        else init[a] = '';
      });
      setScores(init);
    }, [isOpen, application, rows]);

    const previewFinal = useMemo(
      () => computeExamFinalPreview(rows, scores, scoreMode),
      [rows, scores, scoreMode]
    );

    const handleChange = useCallback((area, v) => {
      setScores((s) => ({ ...s, [area]: v }));
    }, []);

    const handleSubmit = useCallback(async () => {
      if (!application?.id) return;
      if (!definitiveAnswerKeyReady) {
        addNotification('Gabarito', DEFINITIVE_ANSWER_KEY_REQUIRED_MSG, 'warning');
        return;
      }
      const parsed = {};
      for (const r of rows) {
        const area = (r.area || '').trim();
        const raw = (scores[area] ?? '').toString().trim().replace(',', '.');
        if (raw === '') {
          addNotification('Preencha todas as áreas', `Faltam acertos em "${area}"`, 'warning');
          return;
        }
        const n = parseFloat(raw);
        if (Number.isNaN(n) || n < 0) {
          addNotification('Acertos inválidos', `Área "${area}"`, 'warning');
          return;
        }
        const maxQ = areaScoreMax(r);
        if (maxQ != null && n > maxQ) {
          addNotification('Acertos acima do limite', `"${area}": máximo ${maxQ} questões`, 'warning');
          return;
        }
        parsed[area] = n;
      }
      try {
        setSaving(true);
        const { data, error } = await apiClient.putExamGrades(application.id, parsed);
        if (error) throw new Error(error);
        const elig = data?.essay_eligibility;
        const eligNote =
          elig?.ok && elig.eligible_count != null
            ? ` Corte redação: ${elig.eligible_count} elegível(is) no processo.`
            : '';
        addNotification('Notas salvas', `${savedGradesNotificationText(scoreMode)}${eligNote}`, 'success');
        onSaved?.(data);
        onClose();
      } catch (e) {
        addNotification('Erro', e.message || 'Falha ao salvar', 'error');
      } finally {
        setSaving(false);
      }
    }, [application, rows, scores, addNotification, onSaved, onClose, definitiveAnswerKeyReady, scoreMode]);

    if (!application) return null;

    const scoreFields = (
      <>
        <p style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.55, margin: '0 0 12px 0' }}>
          {gradeEntryHelpText(scoreMode)}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map((r) => {
            const area = (r.area || '').trim();
            const w = r.weight != null && r.weight !== '' ? r.weight : '—';
            const qc = r.question_count != null && r.question_count !== '' ? r.question_count : '—';
            return (
              <div key={area} style={{ ...cardStyle, padding: '10px 12px' }}>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600, color: '#111827', fontSize: 13 }}>{area}</div>
                    <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>
                      {qc !== '—' ? `${qc} questões` : 'Questões —'}
                      {w !== '—' ? ` · peso ${w}` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <label htmlFor={`grade-${area}`} style={{ fontSize: 11, color: '#6B7280' }}>
                      Acertos{areaScoreMax(r) != null ? ` (0–${areaScoreMax(r)})` : ''}
                    </label>
                    <input
                      id={`grade-${area}`}
                      type="text"
                      inputMode="numeric"
                      value={scores[area] ?? ''}
                      onChange={(e) => handleChange(area, e.target.value)}
                      placeholder={areaScoreMax(r) != null ? `0–${areaScoreMax(r)}` : '0'}
                      style={{
                        ...styles.input,
                        width: '72px',
                        textAlign: 'center',
                        fontSize: 15,
                        fontWeight: 600,
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div
          style={{
            marginTop: 14,
            padding: '12px 14px',
            borderRadius: 10,
            background: '#ECFDF5',
            border: '1px solid #A7F3D0',
          }}
        >
          <div style={{ fontSize: 12, color: '#065F46', fontWeight: 600, marginBottom: 4 }}>
            Prévia da nota objetiva
          </div>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#047857' }}>
            {previewFinal != null
              ? objectiveFinalDisplay(previewFinal, scoreMode, objectiveMax, hasEssay)
              : 'Preencha todas as áreas'}
          </div>
        </div>
      </>
    );

    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`Lançar acertos — inscrição #${application.id}`}
        large={hasScan ? 'conference' : true}
      >
        <div
          style={{
            ...cardStyle,
            marginBottom: hasScan ? 10 : 14,
            padding: hasScan ? '10px 14px' : cardStyle.padding,
            background: 'linear-gradient(135deg, #EFF6FF 0%, #F8FAFC 100%)',
            borderColor: '#BFDBFE',
            flexShrink: 0,
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 600, color: '#1E3A8A', marginBottom: 4 }}>
            {application.candidate_name || 'Candidato'}
          </div>
          <div style={{ fontSize: 13, color: '#475569' }}>{application.course_name || 'Curso'}</div>
          <div style={{ fontSize: 12, color: '#64748B', marginTop: 6 }}>
            Cálculo: <strong>{objectiveScoreModeLabel(scoreMode)}</strong>
            {hasScan ? ' · Folha e gabarito à esquerda; acertos à direita' : ''}
          </div>
        </div>

        {!definitiveAnswerKeyReady && (
          <p
            style={{
              fontSize: 13,
              color: '#92400E',
              background: '#FFFBEB',
              padding: 12,
              borderRadius: 8,
              border: '1px solid #FCD34D',
              marginBottom: 14,
            }}
          >
            {DEFINITIVE_ANSWER_KEY_REQUIRED_MSG}
          </p>
        )}

        {rows.length === 0 ? (
          <p style={{ color: '#B45309', fontSize: 14 }}>
            Este curso não tem áreas da prova no Gabarito. Configure em <strong>Processos → Gabarito</strong>.
          </p>
        ) : (
          <>
            {hasScan ? (
              <div
                style={{
                  display: 'flex',
                  flex: 1,
                  gap: 0,
                  alignItems: 'stretch',
                  minHeight: 0,
                  maxHeight: 'calc(94vh - 220px)',
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    flex: '1 1 62%',
                    minWidth: 0,
                    paddingRight: 16,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <ExamObjectiveOverlayPanel
                    applicationId={application.id}
                    processId={pid}
                    variant="large"
                  />
                </div>
                <div
                  style={{
                    flex: '0 0 min(380px, 34%)',
                    maxWidth: 400,
                    borderLeft: '1px solid #E5E7EB',
                    paddingLeft: 16,
                    overflowY: 'auto',
                    maxHeight: 'calc(94vh - 220px)',
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: 10,
                      position: 'sticky',
                      top: 0,
                      background: '#fff',
                      paddingBottom: 6,
                      zIndex: 1,
                    }}
                  >
                    Lançamento por área
                  </div>
                  {scoreFields}
                </div>
              </div>
            ) : (
              scoreFields
            )}

            <div
              style={{
                display: 'flex',
                gap: 10,
                justifyContent: 'flex-end',
                marginTop: 8,
                flexShrink: 0,
                paddingTop: 8,
                borderTop: hasScan ? '1px solid #E5E7EB' : undefined,
              }}
            >
              <button
                type="button"
                onClick={onClose}
                style={{ ...styles.button, ...styles.buttonSecondary }}
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                style={{ ...styles.button, ...styles.buttonPrimary }}
                disabled={saving || !definitiveAnswerKeyReady}
              >
                {saving ? 'Salvando…' : 'Confirmar acertos'}
              </button>
            </div>
          </>
        )}
      </Modal>
    );
  }
);
